import { Link } from "react-router-dom";
import {
  Lightning, ChartLineUp, Robot, ShieldCheck, WhatsappLogo, FileText,
  Receipt, Bell, CurrencyDollar, MapPin, Check, ArrowRight, Users, Clock,
  Star, CaretRight,
} from "@phosphor-icons/react";

// Número de WhatsApp de soporte (Ecuador) sin el 0 inicial para wa.me.
const WSP_INTL = "593999725015";
const wsp = (text) =>
  `https://wa.me/${WSP_INTL}?text=${encodeURIComponent(text)}`;

const BENEFITS = [
  {
    icon: Robot,
    title: "IA que redacta por ti",
    body: "Agentes GPT-4o generan mensajes de cobro personalizados según el perfil de riesgo del cliente. Tono profesional-cercano, listo para copiar.",
  },
  {
    icon: WhatsappLogo,
    title: "WhatsApp con datos bancarios",
    body: "Cada mensaje incluye automáticamente tu banco, cuenta y RUC. Tu cliente recibe los datos listos sin que tengas que escribirlos cada vez.",
  },
  {
    icon: Receipt,
    title: "OCR de comprobantes",
    body: "Sube la captura del comprobante y la IA valida beneficiario, monto y fecha. Detecta transferencias a cuentas ajenas al vuelo.",
  },
  {
    icon: FileText,
    title: "Cotizaciones y balances en PDF",
    body: "Genera cotizaciones profesionales con IVA 15% (Ecuador) y balances ejecutivos listos para enviar al contador o inversionistas.",
  },
  {
    icon: Bell,
    title: "Recordatorios automáticos",
    body: "El sistema envía recordatorios preventivos la víspera del vencimiento y hace follow-up firme cuando la promesa se rompe.",
  },
  {
    icon: ChartLineUp,
    title: "Dashboard en tiempo real",
    body: "KPIs de tu cartera, semáforo de promesas, cartera vencida vs. venta a crédito, y agenda cronológica de gestión.",
  },
];

const STEPS = [
  {
    n: "01",
    title: "Carga tu cartera",
    body: "Sube tu Excel de deudores (o úsanos con una plantilla lista). En segundos tu cartera queda clasificada por riesgo.",
  },
  {
    n: "02",
    title: "Genera mensajes con IA",
    body: "Con un click, Recobro redacta el mensaje ideal para cada cliente e inyecta tus datos bancarios. Copias, pegas en WhatsApp, listo.",
  },
  {
    n: "03",
    title: "Valida comprobantes al instante",
    body: "Recibes el comprobante, la IA lo valida por OCR y actualiza tu agenda automáticamente. Sin planillas, sin errores manuales.",
  },
];

const PLANS = [
  {
    name: "Básico",
    price: "12.99",
    tagline: "Para autónomos y microempresas",
    limit: "Hasta 30 deudores",
    features: [
      "Cartera y ventas a crédito",
      "Mensajes de cobro con IA",
      "WhatsApp con datos bancarios",
      "Recordatorios automáticos",
      "Educación financiera",
      "Balance PDF ejecutivo",
    ],
    excluded: ["Cotizaciones", "Comprobantes con OCR"],
    cta: "Contratar Básico",
    highlight: false,
    ctaMsg: "Hola, quiero contratar el plan Básico ($12.99) de Recobro.",
  },
  {
    name: "Pro",
    price: "19.99",
    tagline: "Para pymes que crecen rápido",
    limit: "Hasta 150 deudores",
    features: [
      "Todo lo del plan Básico",
      "Cotizaciones ilimitadas con PDF",
      "Comprobantes con OCR (IA visión)",
      "Hasta 5 comprobantes validados",
      "Agenda de promesas",
      "Cuentas por pagar",
      "Soporte prioritario",
    ],
    excluded: [],
    cta: "Contratar Pro",
    highlight: true,
    ctaMsg: "Hola, quiero contratar el plan Pro ($19.99) de Recobro.",
  },
  {
    name: "Enterprise",
    price: "49.99",
    tagline: "Para empresas con cartera grande",
    limit: "Hasta 500 deudores",
    features: [
      "Todo lo del plan Pro",
      "Multi-usuario (próximamente)",
      "Onboarding personalizado",
      "Integración con contabilidad",
      "Soporte 24/7 vía WhatsApp",
      "Reportes personalizados",
    ],
    excluded: [],
    cta: "Contratar Enterprise",
    highlight: false,
    ctaMsg: "Hola, quiero contratar el plan Enterprise ($49.99) de Recobro.",
  },
];

const TESTIMONIOS = [
  {
    quote:
      "Antes tardaba semanas en cuadrar quién me debía. Ahora en 5 minutos sé exactamente a quién contactar y con qué mensaje.",
    author: "María F.",
    role: "Panadería San Andrés · Quito",
  },
  {
    quote:
      "El OCR de comprobantes me salvó de dos fraudes en el primer mes. Detectó que la transferencia iba a otra cuenta.",
    author: "Diego R.",
    role: "Ferretería Los Andes · Cuenca",
  },
  {
    quote:
      "Los recordatorios automáticos organizaron mi día. Ahora sé exactamente a quién debo hacerle seguimiento cada mañana.",
    author: "Carla P.",
    role: "Consultora contable · Guayaquil",
  },
];

const FAQ = [
  {
    q: "¿Recobro envía los WhatsApp automáticamente?",
    a: "Genera el mensaje personalizado y abre WhatsApp con el texto y datos bancarios listos. Tú das el último tap para respetar tu relación con el cliente. Próximamente envío 100% automático vía Twilio.",
  },
  {
    q: "¿Necesito tarjeta de crédito para empezar?",
    a: "No. Contratas vía WhatsApp con nuestro equipo y pagas por transferencia bancaria en Ecuador.",
  },
  {
    q: "¿Mis datos están seguros?",
    a: "Sí. Toda la información viaja cifrada (HTTPS), las contraseñas se guardan con bcrypt + Fernet, y cada empresa tiene su propio espacio aislado (multi-tenant). Cumplimos la LOPDP ecuatoriana.",
  },
  {
    q: "¿Cuántos deudores puedo cargar?",
    a: "Depende del plan: 30 en Básico, 150 en Pro, 500 en Enterprise. Si necesitas más, contáctanos y armamos un plan a medida.",
  },
  {
    q: "¿Recobro actúa como intermediario de pagos?",
    a: "No. Recobro no recauda, retiene ni transfiere dinero. Los pagos van directamente a las cuentas bancarias que tú configuras. Somos una herramienta de organización y asistencia.",
  },
];

// ─── Sección: Nav ────────────────────────────────────────────────────────
function LandingNav() {
  return (
    <nav
      className="sticky top-0 z-40 bg-background/80 backdrop-blur-md border-b border-border"
      data-testid="landing-nav"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
        <Link to="/landing" className="flex items-center gap-2.5">
          <div
            className="w-8 h-8 rounded-md bg-primary text-primary-foreground grid place-items-center font-bold text-xl leading-none"
            style={{ fontFamily: '"Times New Roman", Times, serif' }}
          >
            R
          </div>
          <span className="font-heading font-semibold text-lg tracking-tight">Recobro</span>
        </Link>
        <div className="hidden md:flex items-center gap-8 text-sm text-foreground/80">
          <a href="#beneficios" className="hover:text-primary transition-colors">Beneficios</a>
          <a href="#como-funciona" className="hover:text-primary transition-colors">Cómo funciona</a>
          <a href="#precios" className="hover:text-primary transition-colors">Precios</a>
          <a href="#faq" className="hover:text-primary transition-colors">FAQ</a>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/login"
            data-testid="nav-login-btn"
            className="text-sm font-medium text-foreground/80 hover:text-primary transition-colors"
          >
            Iniciar sesión
          </Link>
          <a
            href={wsp("Hola, quiero saber más sobre Recobro.")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-cta-btn"
            className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity"
          >
            <WhatsappLogo size={16} weight="bold" /> Empezar
          </a>
        </div>
      </div>
    </nav>
  );
}

// ─── Sección: Hero ───────────────────────────────────────────────────────
function Hero() {
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-background via-secondary/40 to-background pt-16 pb-24 md:pt-24 md:pb-32">
      {/* Grano decorativo */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-16 items-center">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-medium mb-6">
            <Lightning size={12} weight="fill" />
            Hecho en Ecuador · Para pymes ecuatorianas
          </div>
          <h1
            className="font-heading font-semibold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-foreground"
            data-testid="hero-title"
          >
            Organiza tu cartera
            <br />
            <span className="text-primary">con la ayuda de la IA.</span>
          </h1>
          <p className="mt-6 text-lg text-foreground/70 max-w-xl leading-relaxed">
            Recobro es la plataforma con agentes de IA que redactan tus mensajes de cobro, validan tus comprobantes y mantienen tu cartera al día. Menos tiempo organizando pagos, más tiempo creciendo tu pyme.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href={wsp("Hola, quiero probar Recobro para mi empresa.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-primary"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-md bg-primary text-primary-foreground font-medium hover:opacity-90 transition-all shadow-lg shadow-primary/20"
            >
              <WhatsappLogo size={18} weight="bold" /> Empezar ahora
              <ArrowRight size={16} weight="bold" />
            </a>
            <a
              href="#precios"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-md border border-border bg-card font-medium hover:border-primary/40 transition-colors"
            >
              Ver planes <CaretRight size={14} weight="bold" />
            </a>
          </div>
          <div className="mt-10 flex flex-wrap gap-6 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Check size={16} weight="bold" className="text-primary" />
              Sin tarjeta al empezar
            </div>
            <div className="flex items-center gap-2">
              <Check size={16} weight="bold" className="text-primary" />
              Datos cifrados
            </div>
            <div className="flex items-center gap-2">
              <Check size={16} weight="bold" className="text-primary" />
              Soporte por WhatsApp
            </div>
          </div>
        </div>
        {/* Mockup / Imagen */}
        <div className="relative">
          <div className="absolute -inset-4 bg-primary/10 rounded-3xl blur-2xl" />
          <div className="relative rounded-2xl overflow-hidden border border-border shadow-2xl">
            <img
              src="https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&w=1200&q=80"
              alt="Emprendedor ecuatoriano usando Recobro"
              className="w-full aspect-[4/5] object-cover"
              loading="eager"
            />
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-background/95 via-background/70 to-transparent p-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/15 grid place-items-center">
                  <ChartLineUp size={20} weight="duotone" className="text-primary" />
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Cartera gestionada este mes</div>
                  <div className="font-heading font-semibold text-xl text-primary">142 clientes</div>
                </div>
              </div>
            </div>
          </div>
          {/* Floating card */}
          <div className="absolute -bottom-6 -left-6 hidden sm:block bg-card border border-border rounded-xl p-4 shadow-xl max-w-[220px]">
            <div className="flex items-center gap-2 mb-1.5">
              <Robot size={16} weight="duotone" className="text-primary" />
              <span className="text-xs font-medium">Agente IA activo</span>
            </div>
            <div className="text-xs text-muted-foreground">
              Redactando 12 mensajes personalizados…
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Sección: Social proof (logos ficticios simples) ─────────────────────
function SocialProof() {
  return (
    <section className="py-10 border-y border-border bg-secondary/30">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center text-xs uppercase tracking-widest text-muted-foreground mb-6">
          Pymes ecuatorianas que ya confían en Recobro
        </div>
        <div className="flex flex-wrap items-center justify-center gap-8 md:gap-14 opacity-60">
          {[
            "PANADERÍA SAN ANDRÉS",
            "TEXTILES OTAVALO",
            "FERRETERÍA LOS ANDES",
            "AGRO ECUATORIANA",
            "HOTEL MIRADOR",
          ].map((brand) => (
            <div
              key={brand}
              className="font-heading text-xs md:text-sm font-semibold tracking-widest text-foreground/60"
            >
              {brand}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Sección: Beneficios ─────────────────────────────────────────────────
function Benefits() {
  return (
    <section id="beneficios" className="py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl mb-14">
          <div className="overline text-primary mb-3">Beneficios</div>
          <h2 className="font-heading font-semibold text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.1]">
            Todo lo que necesitas para
            <br />
            <span className="text-primary">mantener tu cartera al día.</span>
          </h2>
          <p className="mt-5 text-base text-foreground/70">
            Recobro combina agentes de IA, lectura de comprobantes y flujos automatizados diseñados específicamente para la realidad de las pymes en Ecuador.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {BENEFITS.map((b, i) => {
            const Icon = b.icon;
            return (
              <div
                key={b.title}
                data-testid={`benefit-card-${i}`}
                className="group bg-card border border-border rounded-xl p-6 hover:border-primary/40 hover:shadow-lg transition-all duration-300"
              >
                <div className="w-11 h-11 rounded-lg bg-primary/10 grid place-items-center text-primary mb-4 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                  <Icon size={22} weight="duotone" />
                </div>
                <h3 className="font-heading font-semibold text-lg mb-2">{b.title}</h3>
                <p className="text-sm text-foreground/70 leading-relaxed">{b.body}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─── Sección: Cómo funciona ──────────────────────────────────────────────
function HowItWorks() {
  return (
    <section id="como-funciona" className="py-20 md:py-28 bg-secondary/40 border-y border-border">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl mb-14">
          <div className="overline text-primary mb-3">Cómo funciona</div>
          <h2 className="font-heading font-semibold text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.1]">
            De cartera caótica a cartera al día en <span className="text-primary">3 pasos.</span>
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6 md:gap-8">
          {STEPS.map((s) => (
            <div
              key={s.n}
              className="relative bg-card border border-border rounded-xl p-7 hover:shadow-xl transition-all"
            >
              <div className="font-heading font-bold text-5xl text-primary/20 mb-3">{s.n}</div>
              <h3 className="font-heading font-semibold text-xl mb-2">{s.title}</h3>
              <p className="text-sm text-foreground/70 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Sección: Dashboard preview ──────────────────────────────────────────
function DashboardPreview() {
  return (
    <section className="py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <div className="overline text-primary mb-3">Dashboard</div>
          <h2 className="font-heading font-semibold text-3xl md:text-4xl tracking-tight leading-[1.1]">
            Tu cartera, con la claridad de un CFO.
          </h2>
          <p className="mt-5 text-foreground/70 leading-relaxed">
            Semáforo visual de promesas de pago, distribución por riesgo con IA, actividad de tu cartera en tiempo real y el top 5 de clientes que debes gestionar hoy. Todo desde tu celular o computador.
          </p>
          <ul className="mt-6 space-y-3">
            {[
              "Semáforo azul/naranja/rojo en promesas",
              "Distribución por riesgo con IA",
              "Balance ejecutivo en PDF con un click",
              "Notificaciones inteligentes en tiempo real",
            ].map((item) => (
              <li key={item} className="flex items-center gap-3 text-sm">
                <div className="w-5 h-5 rounded-full bg-primary/10 grid place-items-center flex-shrink-0">
                  <Check size={12} weight="bold" className="text-primary" />
                </div>
                {item}
              </li>
            ))}
          </ul>
        </div>
        <div className="relative">
          <div className="absolute -inset-4 bg-primary/5 rounded-3xl blur-3xl" />
          <div className="relative rounded-2xl overflow-hidden border border-border shadow-2xl bg-card">
            <img
              src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1200&q=80"
              alt="Dashboard financiero de Recobro"
              className="w-full aspect-[4/3] object-cover"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Sección: Testimonios ────────────────────────────────────────────────
function Testimonios() {
  return (
    <section className="py-20 md:py-28 bg-primary text-primary-foreground">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl mb-12">
          <div className="overline text-primary-foreground/70 mb-3">Historias reales</div>
          <h2 className="font-heading font-semibold text-3xl md:text-4xl tracking-tight leading-[1.05]">
            Emprendedores ecuatorianos que ya tienen su cartera al día.
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {TESTIMONIOS.map((t, i) => (
            <div
              key={i}
              data-testid={`testimonio-${i}`}
              className="bg-primary-foreground/5 backdrop-blur border border-primary-foreground/10 rounded-xl p-6"
            >
              <div className="flex gap-0.5 mb-4">
                {[...Array(5)].map((_, k) => (
                  <Star key={k} size={14} weight="fill" className="text-chart-3" />
                ))}
              </div>
              <p className="text-sm leading-relaxed opacity-90">"{t.quote}"</p>
              <div className="mt-5 pt-4 border-t border-primary-foreground/10">
                <div className="font-medium text-sm">{t.author}</div>
                <div className="text-xs opacity-70">{t.role}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Sección: Precios ────────────────────────────────────────────────────
function Pricing() {
  return (
    <section id="precios" className="py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="overline text-primary mb-3">Precios en dólares (USD)</div>
          <h2 className="font-heading font-semibold text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.1]">
            Planes que crecen contigo.
          </h2>
          <p className="mt-4 text-foreground/70">
            Contrata en minutos vía WhatsApp. Sin contratos largos, cambia de plan cuando quieras.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {PLANS.map((p, i) => (
            <div
              key={p.name}
              data-testid={`plan-card-${p.name.toLowerCase()}`}
              className={`relative bg-card border rounded-2xl p-7 flex flex-col transition-all ${
                p.highlight
                  ? "border-primary shadow-2xl shadow-primary/10 md:-translate-y-4"
                  : "border-border hover:border-primary/30"
              }`}
            >
              {p.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-primary text-primary-foreground text-xs font-semibold rounded-full">
                  Más elegido
                </div>
              )}
              <div className="mb-6">
                <div className="font-heading font-bold text-2xl">{p.name}</div>
                <div className="text-xs text-muted-foreground mt-1">{p.tagline}</div>
              </div>
              <div className="mb-4 flex items-baseline gap-1">
                <span className="text-xs text-muted-foreground">USD</span>
                <span className="font-heading font-bold text-5xl tracking-tight">${p.price}</span>
                <span className="text-sm text-muted-foreground">/mes</span>
              </div>
              <div className="inline-flex items-center gap-1.5 text-xs text-primary bg-primary/10 px-2.5 py-1 rounded-full self-start mb-6">
                <Users size={12} weight="bold" /> {p.limit}
              </div>
              <ul className="space-y-2.5 mb-8 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <Check size={16} weight="bold" className="text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-foreground/85">{f}</span>
                  </li>
                ))}
                {p.excluded.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm opacity-40 line-through">
                    <Check size={16} weight="bold" className="mt-0.5 flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <a
                href={wsp(p.ctaMsg)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`plan-cta-${p.name.toLowerCase()}`}
                className={`inline-flex items-center justify-center gap-2 w-full px-5 py-3 rounded-md font-medium transition-all ${
                  p.highlight
                    ? "bg-primary text-primary-foreground hover:opacity-90 shadow-lg shadow-primary/25"
                    : "border border-border hover:border-primary hover:bg-primary hover:text-primary-foreground"
                }`}
              >
                <WhatsappLogo size={16} weight="bold" /> {p.cta}
              </a>
            </div>
          ))}
        </div>
        <div className="mt-10 text-center">
          <p className="text-sm text-muted-foreground">
            ¿Necesitas más de 500 deudores o un plan corporativo?
            {" "}
            <a
              href={wsp("Hola, necesito un plan personalizado para mi empresa.")}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary font-medium hover:underline"
            >
              Habla con nosotros por WhatsApp
            </a>
          </p>
        </div>
      </div>
    </section>
  );
}

// ─── Sección: Disponibilidad (solo Ecuador) ──────────────────────────────
function Availability() {
  return (
    <section className="py-20 md:py-24 bg-secondary/40 border-y border-border">
      <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-medium mb-5">
          <MapPin size={12} weight="fill" />
          Disponibilidad
        </div>
        <h2 className="font-heading font-semibold text-3xl md:text-4xl tracking-tight leading-[1.1] max-w-3xl mx-auto">
          Diseñado y operado <span className="text-primary">100% en Ecuador.</span>
        </h2>
        <p className="mt-5 text-foreground/70 leading-relaxed max-w-2xl mx-auto">
          Recobro habla el idioma, maneja la moneda (USD) y respeta las normas del Ecuador. Nuestro equipo responde en español por WhatsApp, y cumplimos con la Ley Orgánica de Protección de Datos Personales (LOPDP) del Ecuador.
        </p>
        <div className="mt-8 inline-flex items-center gap-3 px-5 py-3 rounded-full bg-card border border-border">
          <span className="text-3xl" aria-hidden>🇪🇨</span>
          <div className="text-left">
            <div className="font-semibold text-sm">Ecuador</div>
            <div className="text-xs text-muted-foreground">Quito · Guayaquil · Cuenca · Todo el país</div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Sección: FAQ ────────────────────────────────────────────────────────
function FAQSection() {
  return (
    <section id="faq" className="py-20 md:py-28">
      <div className="max-w-3xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-12">
          <div className="overline text-primary mb-3">Preguntas frecuentes</div>
          <h2 className="font-heading font-semibold text-3xl md:text-4xl tracking-tight leading-[1.1]">
            Todo lo que quieres saber.
          </h2>
        </div>
        <div className="space-y-3">
          {FAQ.map((item, i) => (
            <details
              key={i}
              data-testid={`faq-item-${i}`}
              className="group bg-card border border-border rounded-lg overflow-hidden"
            >
              <summary className="flex items-center justify-between p-5 cursor-pointer list-none hover:bg-secondary/40 transition-colors">
                <span className="font-medium text-sm md:text-base">{item.q}</span>
                <CaretRight
                  size={16}
                  weight="bold"
                  className="text-muted-foreground group-open:rotate-90 transition-transform"
                />
              </summary>
              <div className="px-5 pb-5 text-sm text-foreground/70 leading-relaxed">
                {item.a}
              </div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Sección: CTA Final ──────────────────────────────────────────────────
function FinalCTA() {
  return (
    <section className="py-20 md:py-28 bg-gradient-to-br from-primary via-primary to-primary/90 text-primary-foreground text-center relative overflow-hidden">
      <div
        aria-hidden
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 20%, rgba(255,255,255,0.4), transparent 40%), radial-gradient(circle at 80% 80%, rgba(255,255,255,0.3), transparent 40%)",
        }}
      />
      <div className="relative max-w-3xl mx-auto px-6 lg:px-10">
        <h2 className="font-heading font-semibold text-3xl md:text-5xl tracking-tight leading-[1.05]">
          Deja de perder tiempo
          <br />
          persiguiendo mensajes.
        </h2>
        <p className="mt-6 opacity-90 text-lg">
          Contrata tu plan en 2 minutos por WhatsApp. Sin tarjeta, sin contratos.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <a
            href={wsp("Hola, quiero empezar con Recobro. ¿Qué plan me recomiendan?")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="final-cta-btn"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-md bg-primary-foreground text-primary font-semibold hover:opacity-90 transition-opacity"
          >
            <WhatsappLogo size={18} weight="bold" /> Contactar por WhatsApp
          </a>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-md border border-primary-foreground/40 font-medium hover:bg-primary-foreground/10 transition-colors"
          >
            Crear cuenta gratis <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
        <p className="mt-8 text-xs opacity-70 flex items-center justify-center gap-2">
          <Clock size={12} /> Respuesta promedio del equipo: menos de 30 minutos
        </p>
      </div>
    </section>
  );
}

// ─── Footer ──────────────────────────────────────────────────────────────
function Footer() {
  return (
    <footer className="border-t border-border bg-background py-10">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row justify-between gap-6 text-sm text-muted-foreground">
        <div className="flex items-center gap-2.5">
          <div
            className="w-7 h-7 rounded-md bg-primary text-primary-foreground grid place-items-center font-bold text-base leading-none"
            style={{ fontFamily: '"Times New Roman", Times, serif' }}
          >
            R
          </div>
          <span>© 2026 Recobro · Hecho con ♥ en Ecuador</span>
        </div>
        <div className="flex flex-wrap gap-6">
          <Link to="/terminos" className="hover:text-primary transition-colors">Términos</Link>
          <a
            href={wsp("Hola, necesito soporte con Recobro.")}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-primary transition-colors"
          >
            Soporte
          </a>
          <Link to="/login" className="hover:text-primary transition-colors">Iniciar sesión</Link>
        </div>
      </div>
    </footer>
  );
}

// ─── Página completa ─────────────────────────────────────────────────────
export default function LandingPage() {
  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      <LandingNav />
      <Hero />
      <SocialProof />
      <Benefits />
      <HowItWorks />
      <DashboardPreview />
      <Testimonios />
      <Pricing />
      <Availability />
      <FAQSection />
      <FinalCTA />
      <Footer />
    </div>
  );
}
